/**
 * The rendition of a lot photo a surface should load (2026-09-14).
 *
 * Lot photos come straight from the auction houses' CDNs (spec 016: the
 * optimizer's container timed out fetching them), so the ONLY lever on their
 * weight is which rendition the page asks for — and every surface asked for
 * the largest one. Measured on the live site:
 *
 *   IAAI `large` (845×633)     108–960 KB      the resizer takes `quality`:
 *                                              quality=65 → 55–72 KB;
 *                                              640×480 at 65 → 40–56 KB;
 *                                              400×300 → 27 KB
 *   Copart `_hrs` (1280×960)   ~205 KB         `_ful` is 960×720 at ~126 KB;
 *                                              `_thb` is 144×108, too small
 *
 * A 256px board tile was loading an 845px, 700 KB JPEG — twenty of them on
 * the homepage, twenty-four on the listing. The surfaces:
 *
 *   tile   board tiles, the comparable strip (≤ 380px wide, 2× screens)
 *   card   the listing card's photo half (≤ ~700px)
 *   hero   the lot page's main photo (66vw)
 *   thumb  the lot page's strip of small photos (≤ 160px)
 *
 * The lightbox keeps the original: it is the one place a visitor asks for
 * every pixel. Unknown hosts pass through untouched.
 */

export type TPhotoSurface = 'tile' | 'card' | 'hero' | 'thumb';

const IAAI: Record<TPhotoSurface, { width: number; height: number; quality: number }> = {
  tile: { width: 640, height: 480, quality: 65 },
  card: { width: 845, height: 633, quality: 70 },
  hero: { width: 845, height: 633, quality: 80 },
  thumb: { width: 400, height: 300, quality: 65 },
};

// The card's photo half is ~575px wide on a desktop (1150 on a 2× screen):
// 960px is enough there, and `_ful` is ~40% lighter than `_hrs`. The lot
// page's main photo, the one surface that fills 66vw, keeps `_hrs`.
const COPART: Record<TPhotoSurface, '_ful' | '_hrs'> = { tile: '_ful', card: '_ful', hero: '_hrs', thumb: '_ful' };

export const lotPhotoFor = (url: string, surface: TPhotoSurface): string => {
  if (/^https:\/\/vis\.iaai\.com\/resizer\?/i.test(url)) {
    try {
      const parsed = new URL(url);
      const want = IAAI[surface];

      parsed.searchParams.set('width', String(want.width));
      parsed.searchParams.set('height', String(want.height));
      parsed.searchParams.set('quality', String(want.quality));

      return parsed.toString();
    } catch {
      return url;
    }
  }

  if (/^https:\/\/cs\.copart\.com\//i.test(url)) return url.replace(/_(ful|hrs)\.jpg$/i, `${COPART[surface]}.jpg`);

  return url;
};
